import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import {
  IRange,
  ISelectableEntity,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  ISelectOption,
} from '../../../../data/types';
import { ISlidingDateRangeTypeOption } from '../../../../data/providers/settings/dates/slidingDateRangeProvider';
import { SegmentGroup, ISegmentItem } from '../../../shared/SegmentGroup';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { Select } from '../../../shared/form/Select';
import { SingleMetricSelect } from '../../../shared/form/selects/SingleMetricSelect';
import { TagSelect } from '../../../shared/form/selects/TagsSelect';
import { SlidingDateRangeSelect } from '../../../shared/form/selects/SlidingDateRangeSelect';
import { TeamSelect } from '../../../shared/form/selects/TeamSelect';
import { TestTypeSelect } from '../../../shared/form/selects/TestTypeSelect';
import { IScoreboardSettings } from '../types';

type SegmentType = 'none' | 'team' | 'group';

const SEGMENT_ITEMS: ISegmentItem[] = [
  { value: 'none', label: 'None' },
  { value: 'team', label: 'Team' },
  { value: 'group', label: 'Group' },
];

interface DataTabContentProps {
  settings: IScoreboardSettings;
  segmentType: SegmentType;
  selectedTeam: ISelectOption;
  selectedGroup: ISelectOption;
  selectedLeftColumn: ISelectOption;
  selectedRightColumn: ISelectOption;
  selectedLeftColumnSecondaryFilter: ISelectOption;
  selectedRightColumnSecondaryFilter: ISelectOption;
  groupOptions: ISelectableEntity[];
  filteredGroupOptions: ISelectableEntity[];
  filteredGroupOptionsForSplit: ISelectableEntity[];
  filteredGroupOptionsForLeftColumn: ISelectableEntity[];
  filteredGroupOptionsForRightColumn: ISelectableEntity[];
  onSegmentTypeChange: (details: { value: string }) => void;
  onTeamChange: (team: ISelectOption) => void;
  onGroupChange: (group: ISelectOption) => void;
  onLeftColumnChange: (option: ISelectOption) => void;
  onRightColumnChange: (option: ISelectOption) => void;
  onLeftColumnTagsChange: (tags: ISelectableTag[]) => void;
  onRightColumnTagsChange: (tags: ISelectableTag[]) => void;
  onLeftColumnSecondaryFilterChange: (option: ISelectOption) => void;
  onRightColumnSecondaryFilterChange: (option: ISelectOption) => void;
  onTestTypeChange: (testType: ISelectableTestType) => void;
  onMetricChange: (metric: ISelectableMetric) => void;
  onDateChange: (
    dateRangeType: ISlidingDateRangeTypeOption,
    dateRange: IRange
  ) => void;
  onTagsChange: (tags: ISelectableTag[]) => void;
}

export const DataTabContent = ({
  settings,
  segmentType,
  selectedTeam,
  selectedGroup,
  selectedLeftColumn,
  selectedRightColumn,
  selectedLeftColumnSecondaryFilter,
  selectedRightColumnSecondaryFilter,
  groupOptions,
  filteredGroupOptions,
  filteredGroupOptionsForSplit,
  filteredGroupOptionsForLeftColumn,
  filteredGroupOptionsForRightColumn,
  onSegmentTypeChange,
  onTeamChange,
  onGroupChange,
  onLeftColumnChange,
  onRightColumnChange,
  onLeftColumnTagsChange,
  onRightColumnTagsChange,
  onLeftColumnSecondaryFilterChange,
  onRightColumnSecondaryFilterChange,
  onTestTypeChange,
  onMetricChange,
  onDateChange,
  onTagsChange,
}: DataTabContentProps): React.ReactElement => (
  <VStack gap={4} align='stretch' role='form' aria-label='Scoreboard data configuration'>
    <ControlSpacer style={{ flex: 1 }}>
      <ControlLabel>Split Scoreboard By</ControlLabel>
      <SegmentGroup
        value={segmentType}
        items={SEGMENT_ITEMS}
        onValueChange={onSegmentTypeChange}
        size='sm'
        variant='fullWidth'
      />
    </ControlSpacer>

    {segmentType === 'none' && (
      <>
        <ControlSpacer style={{ flex: 1 }}>
          <ControlLabel id='team-label'>Team</ControlLabel>
          <TeamSelect
            onChange={onTeamChange}
            value={selectedTeam}
            disabled={false}
          />
        </ControlSpacer>
        <Box
          borderWidth='1px'
          borderColor='rgba(255, 255, 255, 0.2)'
          borderRadius='md'
          p={4}
          bg='rgba(255, 255, 255, 0.02)'
          role='group'
          aria-labelledby='filter-label-none'
        >
          <Text
            id='filter-label-none'
            fontSize='sm'
            fontWeight='600'
            color='var(--scoreboard-text-primary, #ffffff)'
            mb={3}
          >
            Filter By (Optional)
          </Text>
          <VStack gap={3} align='stretch'>
            <ControlSpacer style={{ flex: 1 }}>
              <ControlLabel id='group-filter-label-none'>Group</ControlLabel>
              <Select
                options={filteredGroupOptions}
                value={selectedGroup}
                onChange={onGroupChange}
                isDisabled={!selectedTeam}
                placeholder='Select...'
                isClearable
                aria-labelledby='group-filter-label-none'
              />
            </ControlSpacer>
            <ControlSpacer style={{ flex: 1 }}>
              <ControlLabel id='tags-filter-label-none'>Tags</ControlLabel>
              <TagSelect
                value={settings.tags}
                onChange={onTagsChange}
                disabled={!selectedTeam}
                id='tags-filter-none'
              />
            </ControlSpacer>
          </VStack>
        </Box>
      </>
    )}

    {segmentType !== 'none' && (
      <>
        <Flex gap={4} role='group' aria-label='Split column selection'>
          <ControlSpacer style={{ flex: 1 }}>
            <ControlLabel id={`left-column-label-${segmentType}`}>
              Left {segmentType === 'team' ? 'Team' : 'Group'}
            </ControlLabel>
            {segmentType === 'team' ? (
              <TeamSelect
                onChange={onLeftColumnChange}
                value={selectedLeftColumn}
                disabled={false}
              />
            ) : (
              <Select
                options={groupOptions}
                value={selectedLeftColumn}
                onChange={onLeftColumnChange}
                placeholder='Select...'
                isClearable
                aria-labelledby={`left-column-label-${segmentType}`}
              />
            )}
          </ControlSpacer>
          <ControlSpacer style={{ flex: 1 }}>
            <ControlLabel id={`right-column-label-${segmentType}`}>
              Right {segmentType === 'team' ? 'Team' : 'Group'}
            </ControlLabel>
            {segmentType === 'team' ? (
              <TeamSelect
                onChange={onRightColumnChange}
                value={selectedRightColumn}
                disabled={false}
              />
            ) : (
              <Select
                options={groupOptions}
                value={selectedRightColumn}
                onChange={onRightColumnChange}
                placeholder='Select...'
                isClearable
                aria-labelledby={`right-column-label-${segmentType}`}
              />
            )}
          </ControlSpacer>
        </Flex>
        <Flex gap={4} role='group' aria-label='Per-column filters'>
          <Box
            flex={1}
            borderWidth='1px'
            borderColor='rgba(255, 255, 255, 0.2)'
            borderRadius='md'
            p={4}
            bg='rgba(255, 255, 255, 0.02)'
            role='group'
            aria-labelledby='filter-label-left'
          >
            <Text
              id='filter-label-left'
              fontSize='sm'
              fontWeight='600'
              color='var(--scoreboard-text-primary, #ffffff)'
              mb={3}
            >
              Left Column Filters
            </Text>
            <VStack gap={3} align='stretch'>
              {segmentType === 'team' ? (
                <ControlSpacer style={{ flex: 1 }}>
                  <ControlLabel id='group-filter-label-left'>Group</ControlLabel>
                  <Select
                    options={filteredGroupOptionsForLeftColumn}
                    value={selectedLeftColumnSecondaryFilter}
                    onChange={onLeftColumnSecondaryFilterChange}
                    isDisabled={!selectedLeftColumn}
                    placeholder='Select...'
                    isClearable
                    aria-labelledby='group-filter-label-left'
                  />
                </ControlSpacer>
              ) : (
                <ControlSpacer style={{ flex: 1 }}>
                  <ControlLabel id='team-filter-label-left'>Team</ControlLabel>
                  <TeamSelect
                    onChange={onLeftColumnSecondaryFilterChange}
                    value={selectedLeftColumnSecondaryFilter}
                    disabled={!selectedLeftColumn}
                  />
                </ControlSpacer>
              )}
              <ControlSpacer style={{ flex: 1 }}>
                <ControlLabel id='tags-filter-label-left'>Tags</ControlLabel>
                <TagSelect
                  value={settings.splitView?.leftColumn?.tags || []}
                  onChange={onLeftColumnTagsChange}
                  disabled={!selectedLeftColumn}
                  id='tags-filter-left'
                />
              </ControlSpacer>
            </VStack>
          </Box>
          <Box
            flex={1}
            borderWidth='1px'
            borderColor='rgba(255, 255, 255, 0.2)'
            borderRadius='md'
            p={4}
            bg='rgba(255, 255, 255, 0.02)'
            role='group'
            aria-labelledby='filter-label-right'
          >
            <Text
              id='filter-label-right'
              fontSize='sm'
              fontWeight='600'
              color='var(--scoreboard-text-primary, #ffffff)'
              mb={3}
            >
              Right Column Filters
            </Text>
            <VStack gap={3} align='stretch'>
              {segmentType === 'team' ? (
                <ControlSpacer style={{ flex: 1 }}>
                  <ControlLabel id='group-filter-label-right'>Group</ControlLabel>
                  <Select
                    options={filteredGroupOptionsForRightColumn}
                    value={selectedRightColumnSecondaryFilter}
                    onChange={onRightColumnSecondaryFilterChange}
                    isDisabled={!selectedRightColumn}
                    placeholder='Select...'
                    isClearable
                    aria-labelledby='group-filter-label-right'
                  />
                </ControlSpacer>
              ) : (
                <ControlSpacer style={{ flex: 1 }}>
                  <ControlLabel id='team-filter-label-right'>Team</ControlLabel>
                  <TeamSelect
                    onChange={onRightColumnSecondaryFilterChange}
                    value={selectedRightColumnSecondaryFilter}
                    disabled={!selectedRightColumn}
                  />
                </ControlSpacer>
              )}
              <ControlSpacer style={{ flex: 1 }}>
                <ControlLabel id='tags-filter-label-right'>Tags</ControlLabel>
                <TagSelect
                  value={settings.splitView?.rightColumn?.tags || []}
                  onChange={onRightColumnTagsChange}
                  disabled={!selectedRightColumn}
                  id='tags-filter-right'
                />
              </ControlSpacer>
            </VStack>
          </Box>
        </Flex>
      </>
    )}

    <ControlSpacer style={{ flex: 1 }}>
      <ControlLabel id='test-type-label'>Test Type</ControlLabel>
      <TestTypeSelect
        onChange={onTestTypeChange}
        value={settings.testType}
        disabled={false}
      />
    </ControlSpacer>

    <ControlSpacer style={{ flex: 1 }}>
      <ControlLabel id='metric-label'>Metric</ControlLabel>
      <SingleMetricSelect
        onChange={onMetricChange}
        testTypeId={!isEmpty(settings.testType) ? settings.testType.value : ''}
        disabled={isEmpty(settings.testType)}
        value={
          isEmpty(settings.metrics[settings.testType.value])
            ? null
            : settings.metrics[settings.testType.value]
        }
      />
    </ControlSpacer>

    <ControlSpacer>
      <SlidingDateRangeSelect
        dateRangeType={settings.dateRangeType}
        dateRange={settings.dateRange}
        disallowLastSession
        allowEventMode
        onChange={onDateChange}
      />
    </ControlSpacer>
  </VStack>
);
